<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1000">
    <v-card min-height="400">
      <v-card-title>
        Replace "{{ componentName }}"
        <v-spacer/>
        <v-btn
          color="success"
          :disabled="!canReplace"
          :loading="replacing"
          @click="replace"
        >
          Replace
        </v-btn>
        <v-btn icon class="ml-2" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        in {{ mealName }} ({{ mealDate }})
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="!editAllowed && !editAnyway" type="warning" outlined>
          This meal is close to or past its production date. Customer orders may exist
          (ingredient lists, printing, procurement).
          <v-btn class="ml-2" small color="error" @click="editAnyway=true">EDIT ANYWAY</v-btn>
        </v-alert>
        <v-alert v-if="totalAmount===0" type="warning" outlined>
          This meal has no weighed ingredients for this component, so a replacement cannot be scaled.
        </v-alert>
        <v-row>
          <v-col cols="6">
            <h3>Current: {{ componentName }}</h3>
            <div class="caption mb-2">
              <template v-if="totalAmount>0">
                meal uses <b>{{ formatWeightWithUnits(totalAmount) }}</b>. 100g portion below
              </template>
              <template v-else>as used in this meal. 100g portion below</template>
            </div>
            <ul>
              <li v-for="ingredient of current100" v-bind:key="ingredient.name">
                <span :class="isDropped(ingredient) ? 'text-decoration-line-through red--text' : ''">
                  {{ ingredient.amount }}g {{ ingredient.name }}
                </span>
              </li>
            </ul>
            <div v-if="currentCost.hasAnyPrice" class="mt-2">
              cost: <b>${{ formatDollars(currentCost.cost) }}</b> per 100g
              <template v-if="totalAmount>0">
                (${{ formatDollars(currentCost.cost * totalAmount / 100) }}
                for {{ formatWeightWithUnits(totalAmount) }})
              </template>
            </div>
            <div v-if="currentNotesLines.length>0" class="caption grey--text text--darken-1 mt-2">
              <div v-for="(line,i) of currentNotesLines" v-bind:key="i">{{ line }}</div>
            </div>
            <v-alert v-if="currentCost.unpriced.length>0" type="warning" outlined dense class="mt-2">
              {{ missingCostMessage(currentCost) }}
            </v-alert>
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              v-model="replacementId"
              :items="candidateItems"
              label="Replacement component (used in this production)"
              clearable
              autofocus
            />
            <template v-if="candidate">
              <div class="caption mb-2">
                copying the version used in "{{ candidate.sourceMealName }}"
                <v-chip x-small outlined color="green" v-if="candidate.sourceIsDefault">matches default</v-chip>
                <v-chip x-small outlined color="orange" v-else>meal-specific version</v-chip>
                - a 100g portion consists of
              </div>
              <ul>
                <li
                  v-for="ingredient of comparison.items" v-bind:key="ingredient.name"
                  :style="ingredient.changed ? 'font-weight: bolder' : ''"
                >
                  {{ ingredient.amount }}g {{ ingredient.name }}
                </li>
              </ul>
              <div v-if="candidateCost.hasAnyPrice" class="mt-2">
                cost: <b>${{ formatDollars(candidateCost.cost) }}</b> per 100g
                <template v-if="totalAmount>0">
                  (${{ formatDollars(candidateCost.cost * totalAmount / 100) }}
                  for {{ formatWeightWithUnits(totalAmount) }})
                </template>
              </div>
              <div v-if="candidateNotesLines.length>0" class="caption grey--text text--darken-1 mt-2">
                <div v-for="(line,i) of candidateNotesLines" v-bind:key="i">{{ line }}</div>
              </div>
              <v-alert v-if="candidateCost.unpriced.length>0" type="warning" outlined dense class="mt-2">
                {{ missingCostMessage(candidateCost) }}
              </v-alert>
              <v-alert v-if="totalAmount>0" type="info" outlined dense class="mt-4">
                Ingredient amounts will be scaled so the meal still uses
                {{ formatWeightWithUnits(totalAmount) }} of the component after the replacement.
              </v-alert>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import {formatWeightWithUnits} from '@/store/utils';
import {compareIngredients, formatDollars, ingredientCost, normaliseTo100g} from '@/utils/componentIngredients';

const toLines = text => (text || '').split('\n').filter(line => line.trim());

export default {
  name: 'ComponentReplaceDialog',
  props: {
    value: {type: Boolean, default: false},
    mealId: {type: Number, required: false},
    mealName: {type: String, default: ''},
    mealDate: {type: String, default: ''},
    mealSku: {type: String, default: null},
    componentId: {type: Number, required: false},
    componentName: {type: String, default: ''},
    componentNotes: {type: String, default: ''},
    // the meal_food rows of the component being replaced, in grams
    currentIngredients: {type: Array, default: () => []},
    // other components in the production, each carrying the meal version a
    // replacement would copy: [{id, name, sourceMealId, sourceMealName, sourceIsDefault, ingredients}]
    candidates: {type: Array, default: () => []},
    editAllowed: {type: Boolean, default: false},
  },
  data() {
    return {
      replacing: null,
      editAnyway: false,
      replacementId: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.editAnyway = false;
        this.replacementId = null;
      }
    },
  },
  computed: {
    candidateItems() {
      return this.candidates
        .map(c => {
          const {cost, hasAnyPrice} = ingredientCost(normaliseTo100g(c.ingredients));
          let costLabel = 'no cost info';
          if (hasAnyPrice) {
            costLabel = `$${formatDollars(cost)} per 100g`;
            if (this.totalAmount > 0) {
              costLabel += ` ($${formatDollars(cost * this.totalAmount / 100)} for ${formatWeightWithUnits(this.totalAmount)})`;
            }
          }
          return {value: c.id, text: `${c.name} - ${costLabel}`};
        })
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    candidate() {
      return this.candidates.find(c => c.id === this.replacementId);
    },
    totalAmount() {
      return this.currentIngredients.reduce((sum, i) => sum + i.amount, 0);
    },
    current100() {
      return normaliseTo100g(this.currentIngredients);
    },
    comparison() {
      if (!this.candidate) {
        return {items: [], removed: []};
      }
      return compareIngredients(normaliseTo100g(this.candidate.ingredients), this.current100);
    },
    currentCost() {
      return ingredientCost(this.current100);
    },
    currentNotesLines() {
      return toLines(this.componentNotes);
    },
    candidateNotesLines() {
      return toLines(this.candidate && this.candidate.notes);
    },
    candidateCost() {
      if (!this.candidate) {
        return ingredientCost([]);
      }
      return ingredientCost(normaliseTo100g(this.candidate.ingredients));
    },
    canReplace() {
      return (this.editAllowed || this.editAnyway)
        && !!this.candidate
        && !this.replacing
        && this.totalAmount > 0;
    },
  },
  methods: {
    formatWeightWithUnits,
    // an ingredient of the current component that the replacement doesn't have
    isDropped(ingredient) {
      return this.comparison.removed.some(r => r.name === ingredient.name);
    },
    formatDollars,
    missingCostMessage({hasAnyPrice, unpriced}) {
      if (!hasAnyPrice) {
        return 'No cost information available for this component.';
      }
      return `Cost excludes ${unpriced.length} ingredient${unpriced.length === 1 ? '' : 's'} with no price: `
        + unpriced.map(i => i.name).join(', ');
    },
    // the meal name often mentions the replaced component, so offer to update it
    async offerRename() {
      const newName = prompt(
        'The component was replaced. Update the meal name to match? (Cancel keeps the current name)',
        this.mealName
      );
      if (newName === null || !newName.trim() || newName.trim() === this.mealName) {
        return;
      }
      try {
        await api.put(`v2/meal/${this.mealId}`, {meal: {id: this.mealId, name: newName.trim()}});
      } catch (e) {
        console.error('failed to rename meal', e);
        alert(`failed to rename meal: ${e.message}`);
      }
    },
    async replace() {
      const amount = this.totalAmount;
      const candidate = this.candidate;
      const message = `replacing ${formatWeightWithUnits(amount)} of \n"${this.componentName}" with "${candidate.name}" \n(the version used in "${candidate.sourceMealName}") \nin ${this.mealName}\n\nARE YOU SURE?`;
      if (!confirm(message)) {
        console.log('cancelled');
        return;
      }
      this.replacing = true;
      try {
        await api.delete(`/v2/meal/${this.mealId}/component/${this.componentId}`);
        // copy from a production meal's version - the default recipe may be stale
        await api.post(`/v2/meal/${this.mealId}/component/${candidate.id}`, {
          amount,
          source_meal_id: candidate.sourceMealId,
        });
        if (this.mealSku) {
          // keep this edited meal as the SKU's selected version, like MealAudit does
          await api.put(`v2/meal/sku/${this.mealSku}`, {selected_meal_id: this.mealId});
        }
        await this.offerRename();
        this.$emit('replaced');
        this.$emit('input', false);
      } catch (e) {
        console.error('failed to replace component', e);
        alert(`failed to replace component: ${e.message}`);
      } finally {
        this.replacing = false;
      }
    },
  },
};
</script>

<style scoped>

</style>
